import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronLeft, Link2, MoreVertical, Search } from 'lucide-react';
import {
  leadSelectPrimaryLabel,
  leadSelectSecondaryLabel,
  matchesLeadSelectQuery,
} from '../../../utils/leadSelectOption';
import { FILTER_INPUT_AUTOFILL_ATTRS, unlockFilterInputAfterFocus } from '../../../utils/filterInputAutofill';
import { patchInboxEmailLeadLink } from '../inboxLeadLink';
import type { LeadOption } from '../inboxShared';

type Props = {
  emailId: string | number;
  threadId?: string | null;
  currentLeadId?: string | number | null;
  leads: LeadOption[];
  onLinked?: (leadId: string | number | null, warning?: string) => void;
  disabled?: boolean;
  /** `menu` = Drei-Punkte-Menü rechts; `inline` = sichtbarer Chip unter der Mail */
  variant?: 'inline' | 'menu';
};

type MenuStep = 'actions' | 'assign';

type PanelPosition = { left: number; top: number; width: number };

function panelPosition(anchor: DOMRect, width: number, align: 'left' | 'right'): PanelPosition {
  const margin = 8;
  const gap = 4;
  const vw = window.innerWidth;
  const panelWidth = Math.min(width, vw - 2 * margin);
  let left = align === 'right' ? anchor.right - panelWidth : anchor.left;
  left = Math.min(Math.max(left, margin), vw - panelWidth - margin);
  return { left, top: anchor.bottom + gap, width: panelWidth };
}

export function InboxEmailProjectLinker({
  emailId,
  threadId,
  currentLeadId,
  leads,
  onLinked,
  disabled = false,
  variant = 'inline',
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [menuStep, setMenuStep] = React.useState<MenuStep>('actions');
  const [search, setSearch] = React.useState('');
  const [searchAutofillLocked, setSearchAutofillLocked] = React.useState(true);
  const [linking, setLinking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = React.useState<PanelPosition | null>(null);
  const searchFieldId = React.useId();

  const resetMenu = React.useCallback(() => {
    setOpen(false);
    setMenuStep('actions');
    setSearch('');
    setSearchAutofillLocked(true);
    setError(null);
    setPanelPos(null);
  }, []);

  const panelWidth = variant === 'menu' && menuStep === 'actions' ? 192 : 288;
  const panelAlign: 'left' | 'right' = variant === 'menu' ? 'right' : 'left';

  const updatePanelPosition = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    setPanelPos(panelPosition(el.getBoundingClientRect(), panelWidth, panelAlign));
  }, [panelAlign, panelWidth]);

  React.useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      resetMenu();
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open, resetMenu]);

  const current = React.useMemo(
    () => leads.find((l) => String(l.id) === String(currentLeadId ?? '')) ?? null,
    [leads, currentLeadId],
  );

  const filtered = React.useMemo(() => {
    return leads.filter((l) => matchesLeadSelectQuery(l, search));
  }, [leads, search]);

  const apply = async (leadId: string | number | null) => {
    setLinking(true);
    setError(null);
    try {
      const result = await patchInboxEmailLeadLink(emailId, leadId);
      if (result.ok === false) throw new Error(result.error);
      resetMenu();
      onLinked?.(leadId, result.warning);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verknüpfung fehlgeschlagen');
    } finally {
      setLinking(false);
    }
  };

  const threadHint = threadId
    ? 'Gilt für den gesamten E-Mail-Thread.'
    : 'Nur diese einzelne Nachricht (kein Thread).';

  const currentPrimary = current ? leadSelectPrimaryLabel(current) : null;
  const currentSecondary = current ? leadSelectSecondaryLabel(current) : null;

  const assignPanel = (
    <>
      <div className="p-2 border-b border-app-border">
        {variant === 'menu' && (
          <button
            type="button"
            onClick={() => {
              setMenuStep('actions');
              setSearch('');
              setError(null);
            }}
            className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium text-app-text-subtle hover:text-app-text"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Zurück
          </button>
        )}
        <p className="text-[10px] text-app-text-subtle mb-2">{threadHint}</p>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-text-subtle" />
          <input
            id={searchFieldId}
            name={`inbox-project-search-${searchFieldId}`}
            type="text"
            role="searchbox"
            aria-label="Projekt suchen"
            {...FILTER_INPUT_AUTOFILL_ATTRS}
            readOnly={searchAutofillLocked}
            onFocus={(e) => {
              unlockFilterInputAfterFocus(e.currentTarget);
              setSearchAutofillLocked(false);
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Projekt suchen…"
            className="w-full pl-8 pr-2 py-1.5 text-xs border border-app-border rounded-md outline-none focus:ring-1 focus:ring-pipedrive-blue"
          />
        </div>
      </div>
      <div className="max-h-44 overflow-y-auto">
        <button
          type="button"
          onClick={() => void apply(null)}
          disabled={linking}
          className={`w-full text-left px-3 py-2 text-xs hover:bg-app-hover disabled:opacity-60 ${
            currentLeadId == null ? 'bg-pipedrive-blue text-white hover:bg-pipedrive-blue' : 'text-app-text'
          }`}
        >
          — Kein Projekt —
        </button>
        {filtered.map((l) => {
          const selected = String(currentLeadId) === String(l.id);
          const secondary = leadSelectSecondaryLabel(l);
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => void apply(l.id)}
              disabled={linking}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-app-hover disabled:opacity-60 ${
                selected ? 'bg-pipedrive-blue text-white hover:bg-pipedrive-blue' : 'text-app-text'
              }`}
            >
              <span className="block font-medium truncate">{leadSelectPrimaryLabel(l)}</span>
              {secondary ? (
                <span className={`block truncate ${selected ? 'text-white/90' : 'text-app-text-subtle'}`}>
                  {secondary}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {error && <p className="px-3 py-2 text-[10px] text-rose-600 border-t border-app-border">{error}</p>}
    </>
  );

  const panelContent =
    open && panelPos && variant === 'menu' && menuStep === 'actions' ? (
      <div
        ref={panelRef}
        className="rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
        style={{
          position: 'fixed',
          left: panelPos.left,
          top: panelPos.top,
          width: panelPos.width,
          zIndex: 280,
        }}
        role="menu"
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => setMenuStep('assign')}
          className="w-full text-left px-3 py-2 text-xs font-medium text-app-text hover:bg-app-hover"
        >
          E-Mail-Zuweisung ändern
        </button>
      </div>
    ) : open && panelPos ? (
      <div
        ref={panelRef}
        className="rounded-lg border border-app-border bg-app-surface shadow-lg overflow-hidden"
        style={{
          position: 'fixed',
          left: panelPos.left,
          top: panelPos.top,
          width: panelPos.width,
          maxWidth: 'calc(100vw - 2rem)',
          zIndex: 280,
        }}
      >
        {assignPanel}
      </div>
    ) : null;

  const portaledPanel = panelContent ? createPortal(panelContent, document.body) : null;

  if (variant === 'menu') {
    return (
      <div ref={anchorRef} className="relative shrink-0">
        <button
          type="button"
          disabled={disabled || linking}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="E-Mail-Aktionen"
          title={
            current
              ? `Projekt: ${currentPrimary}${currentSecondary ? ` · ${currentSecondary}` : ''}`
              : 'Projekt zuweisen'
          }
          onClick={() => {
            if (open) resetMenu();
            else {
              setOpen(true);
              setMenuStep('actions');
            }
          }}
          className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 disabled:opacity-60"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {portaledPanel}
      </div>
    );
  }

  return (
    <div ref={anchorRef} className="relative">
      <button
        type="button"
        disabled={disabled || linking}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 max-w-full rounded-md border border-app-border bg-app-surface px-2 py-1 text-[11px] font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-60"
        title={threadHint}
      >
        <Link2 className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
        <span className="truncate">
          {currentPrimary
            ? currentSecondary
              ? `${currentPrimary} · ${currentSecondary}`
              : currentPrimary
            : 'Projekt zuweisen…'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {error && !open && <p className="mt-1 text-[10px] text-rose-600">{error}</p>}
      {portaledPanel}
    </div>
  );
}
